import { formatSuccessFeeRate } from '@/libs/utils/format-success-fee-rate';
import { Text, type TextProps } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { formatCollabFeeFloor } from '../plan-fees/format-collab-fee-floor';
import type { PlanChoice } from './plan-choice-offer';

type Group = { key: string; choices: PlanChoice[] };

function groupBy(choices: PlanChoice[], keyOf: (choice: PlanChoice) => string): Group[] {
  const groups: Group[] = [];
  for (const choice of choices) {
    const key = keyOf(choice);
    const group = groups.find((candidate) => candidate.key === key);
    if (group) group.choices.push(choice);
    else groups.push({ key, choices: [choice] });
  }
  return groups;
}

function joinList(items: string[], locale: string): string {
  try {
    return new Intl.ListFormat(locale, { type: 'conjunction' }).format(items);
  } catch {
    return items.join(', ');
  }
}

/**
 * The collab and gifting fees of every plan on screen, in one quiet line under
 * the cards. Read from the choices, grouped where plans charge the same.
 */
export const PlanChoiceFeeNote = ({
  choices,
  currencyCode,
  ...textProps
}: {
  choices: PlanChoice[];
  /** The organization's currency, which writes the minimum when a plan has no price. */
  currencyCode?: string | null;
} & TextProps) => {
  const { t } = useTranslation();
  const locale = t('common.locale');
  const currencyOf = (choice: PlanChoice) => choice.price?.currencyCode ?? currencyCode ?? 'EUR';
  const floorOf = (choice: PlanChoice) =>
    formatCollabFeeFloor(choice.collabFee.floorMinorUnits, currencyOf(choice), locale);
  const plansOf = (group: Group) =>
    joinList(
      group.choices.map((choice) => t(`pricing.plan.${choice.id}.name`)),
      locale
    );

  const collabGroups = groupBy(choices, (choice) => `${choice.collabFee.basisPoints}`);
  const floors = new Set(choices.map(floorOf));
  const uniformFloor = floors.size === 1;
  // Groups are listed with commas: the plans inside a group already read "A and B".
  const collabRates = collabGroups
    .map((group) =>
      t(uniformFloor ? 'planChoice.fees.rate' : 'planChoice.fees.rateWithFloor', {
        rate: formatSuccessFeeRate(group.choices[0]!.collabFee.basisPoints, locale),
        plans: plansOf(group),
        ...(uniformFloor ? {} : { floor: floorOf(group.choices[0]!) }),
      })
    )
    .join(', ');
  const collabs = uniformFloor
    ? t('planChoice.fees.collabs', { rates: collabRates, floor: [...floors][0] })
    : t('planChoice.fees.collabsMixed', { rates: collabRates });

  const giftingGroups = groupBy(choices, (choice) => `${choice.giftingFeeBasisPoints}`);
  const gifting =
    giftingGroups.length === 1 && choices[0]?.giftingFeeBasisPoints === 0
      ? t('planChoice.fees.giftingFree')
      : t('planChoice.fees.gifting', {
          rates: giftingGroups
            .map((group) =>
              t('planChoice.fees.rate', {
                rate: formatSuccessFeeRate(group.choices[0]!.giftingFeeBasisPoints, locale),
                plans: plansOf(group),
              })
            )
            .join(', '),
        });

  if (!choices.length) return null;
  return <Text {...textProps}>{`${collabs} ${gifting}`}</Text>;
};
